import React from 'react';
import { ROUTES_PATH } from '@/resources/routes-name';
import { createColumnPopover } from '../../../../components/table/function/createColumn';
import { Tag } from 'antd';
import { claimInfoColumns, createClaimStatusColumns } from '../../shared/claimColumns';
import { canResendRequest } from '../../claim-detail/hooks/useResendRequest';
import { canResendCs } from '../../claim-detail/hooks/useResendCs';
import { canRevertReject } from '../hooks/useRevertReject';
import { color, Icon } from '../../../../resources';
import { stockAvailabilityColumn } from '../../shared/stockAvailabilityColumn';

const WAIT_APPROVE_KEY = 'WAIT_APPROVE_NO_DEDUCT';
const WAIT_APPROVE_LABEL = 'รออนุมัติ ไม่ตัดประกัน';
const isWaitApprove = (record) => record?.approveStatus === 'WAIT_APPROVE';

export const columns = ({
  onModalMessage = () => {},
  onModalProcessStatus = () => {},
  onModalRemark = () => {},
  onModalChangeTrackNumber = () => {},
  onResendRequest = null,
  isPendingResendRequest = false,
  onResendCs = null,
  isPendingResendCs = false,
  onRevertReject = null,
  isPendingRevertReject = false,
  onShowHistory = () => undefined,
  privilegeAbmType,
  stockMap = {},
  selectedByItem = {},
}) => {
  const { claimStatusColumn, processStatusColumn } = createClaimStatusColumns({
    onModalProcessStatus,
  });

  return [
    {
      ...claimStatusColumn,
      width: 220,
      filters: [
        ...claimStatusColumn.filters.map(({ text, value }) => ({
          text: `สถานะ: ${text}`,
          value: `claim:${value}`,
        })),
        ...processStatusColumn.filters.map(({ text, value }) => ({
          text: `สถานะภายใน: ${text}`,
          value: `process:${value}`,
        })),
        { text: `สถานะภายใน: ${WAIT_APPROVE_LABEL}`, value: `process:${WAIT_APPROVE_KEY}` },
        { text: 'ประกัน: ตัดประกัน', value: 'deduct:N' },
        { text: 'ประกัน: ไม่ตัดประกัน', value: 'deduct:Y' },
      ],
      onFilter: (value, record) => {
        const separator = value.indexOf(':');
        const group = value.slice(0, separator);
        const key = value.slice(separator + 1);
        if (group === 'claim') {
          return claimStatusColumn.onFilter(key, record);
        }
        if (group === 'process') {
          // เคลมรออนุมัติแสดงสถานะภายในเป็น "รออนุมัติ ไม่ตัดประกัน" แทน processStatus จึงกรองตามที่เห็น
          if (key === WAIT_APPROVE_KEY) return isWaitApprove(record);
          return !isWaitApprove(record) && processStatusColumn.onFilter(key, record);
        }
        // ค่าที่ API ยังไม่ส่งมา ถือว่าเป็นเคลมปกติที่ตัดประกัน
        return (record?.noDeductWarranty === 'Y' ? 'Y' : 'N') === key;
      },
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {claimStatusColumn.render(_, record)}
          {/* ไม่ตัดประกัน และเคลมที่เปิดใหม่จากการส่งซ้ำ ถูกตั้ง WAIT_APPROVE ทั้งคู่ */}
          {isWaitApprove(record) ? (
            <Tag color="gold">{WAIT_APPROVE_LABEL}</Tag>
          ) : (
            <>
              {processStatusColumn.render(_, record)}
              {record?.noDeductWarranty === 'Y' && (
                <Tag color={color.orangeTheme}>ไม่ตัดประกัน</Tag>
              )}
            </>
          )}
        </div>
      ),
    },
    stockAvailabilityColumn(stockMap, selectedByItem),
    ...claimInfoColumns({ onModalChangeTrackNumber, onShowHistory }),
    ...(privilegeAbmType === 'READ'
      ? [
          createColumnPopover({
            title: 'จัดการ',
            dataIndex: 'rowNo',
            options: {
              others: {
                fixed: 'right',
                width: 80,
              },
            },
            contentOptions: {
              menuItems: [
                {
                  icon: Icon.detail,
                  text: 'ดูรายละเอียด',
                  color: color.orangeTheme,
                  onClick: (record) => {
                    const url = `${ROUTES_PATH.ROUTE_CLAIM_DETAIL.PATH}?code=${record.claimCode}`;
                    window.open(url, '_blank');
                  },
                },
                {
                  icon: Icon.users,
                  text: 'ประวัติลูกค้า',
                  color: color.search2,
                  onClick: (record) => onShowHistory(record),
                },
              ],
            },
          }),
        ]
      : [
          createColumnPopover({
            title: 'จัดการ',
            dataIndex: 'rowNo',
            options: {
              others: {
                fixed: 'right',
                width: 80,
              },
            },
            contentOptions: {
              menuItems: [
                {
                  icon: Icon.edit2,
                  text: 'แก้ไข',
                  color: color.orangeTheme,
                  visible: (record) => record.claimStatus !== 'TRANSPORTED',
                  onClick: (record) => {
                    const url = `${ROUTES_PATH.ROUTE_CLAIM_DETAIL.PATH}?code=${record.claimCode}`;
                    window.open(url, '_blank');
                  },
                },
                {
                  icon: Icon.detail,
                  text: 'ดูรายละเอียด',
                  color: color.orangeTheme,
                  visible: (record) => record.claimStatus === 'TRANSPORTED',
                  onClick: (record) => {
                    const url = `${ROUTES_PATH.ROUTE_CLAIM_DETAIL.PATH}?code=${record.claimCode}`;
                    window.open(url, '_blank');
                  },
                },
                {
                  icon: Icon.users,
                  text: 'ประวัติลูกค้า',
                  color: color.search2,
                  onClick: (record) => onShowHistory(record),
                },
                {
                  icon: Icon.line,
                  text: 'ส่งข้อความ',
                  color: color.line,
                  onClick: (record) => onModalMessage(record),
                },
                {
                  icon: Icon.evernote,
                  text: 'หมายเหตุ',
                  color: color.search2,
                  onClick: (record) => onModalRemark(record),
                },
                {
                  icon: Icon.transfer,
                  text: 'ส่งซ้ำ',
                  color: color.search2,
                  visible: (record) => !isPendingResendRequest && canResendRequest(record),
                  onClick: (record) => onResendRequest?.(record),
                },
                {
                  icon: Icon.transfer,
                  text: 'ขอเลขเอกสาร CS',
                  color: color.search2,
                  visible: (record) => !!onResendCs && !isPendingResendCs && canResendCs(record),
                  onClick: (record) => onResendCs?.(record),
                },
                {
                  icon: Icon.history,
                  text: 'ถอยสถานะ',
                  color: color.search2,
                  visible: (record) => !isPendingRevertReject && canRevertReject(record),
                  onClick: (record) => onRevertReject?.(record),
                },
              ],
            },
          }),
        ]),
  ];
};

// ลำดับ/ชื่อคอลัมน์ Excel ใช้ร่วมกันทุกแบรนด์ (37 คอลัมน์) แบรนด์ที่ไม่มีข้อมูลจะเว้นว่าง
// ต่อท้ายด้วยสต็อก 2 คอลัมน์ (ค่ามาจาก getStockExportFields ไม่ได้มาจาก API เคลม)
export const exportHeaders = [
  { dataIndex: 'claimCode', title: 'เลขที่เคลม' },
  { dataIndex: 'claimDate', title: 'วันที่เคลม' },
  { dataIndex: 'warrantyCode', title: 'เลขที่ลงทะเบียน' },
  { dataIndex: 'warrantyDate', title: 'วันที่ลงทะเบียน' },
  { dataIndex: 'fullName', title: 'ชื่อ-นามสกุล' },
  { dataIndex: 'lineName', title: 'ชื่อไลน์' },
  { dataIndex: 'lineUid', title: 'ไลน์ UID' },
  { dataIndex: 'telephone', title: 'เบอร์โทร' },
  { dataIndex: 'addressFullText', title: 'ที่อยู่จัดส่ง' },
  { dataIndex: 'subDistrictName', title: 'ตำบล/แขวง' },
  { dataIndex: 'districtName', title: 'อำเภอ/เขต' },
  { dataIndex: 'provinceName', title: 'จังหวัด' },
  { dataIndex: 'zipCode', title: 'รหัสไปรษณีย์' },
  { dataIndex: 'mobileBrandName', title: 'แบรนด์มือถือ' },
  { dataIndex: 'mobileModelName', title: 'รุ่นมือถือ' },
  { dataIndex: 'mobileBrandName2', title: 'แบรนด์มือถือ 2' },
  { dataIndex: 'mobileModelName2', title: 'รุ่นมือถือ 2' },
  { dataIndex: 'mobileBrandName3', title: 'แบรนด์มือถือ 3' },
  { dataIndex: 'mobileModelName3', title: 'รุ่นมือถือ 3' },
  { dataIndex: 'productTypeName', title: 'ประเภทสินค้า' },
  { dataIndex: 'itemCode', title: 'รหัสสินค้า' },
  { dataIndex: 'productName', title: 'ชื่อสินค้า' },
  { dataIndex: 'breakdownName', title: 'สาเหตุการเคลม' },
  { dataIndex: 'breakdownSubNameTh', title: 'สาเหตุการเคลมย่อย' },
  { dataIndex: 'detail1', title: 'รายละเอียด (อาการเสีย)' },
  { dataIndex: 'channelClaimName', title: 'ช่องทางการเคลม' },
  { dataIndex: 'storeName', title: 'สาขาที่เคลม' },
  { dataIndex: 'remainingDays', title: 'วันคงเหลือ' },
  { dataIndex: 'claimStatusName', title: 'สถานะ' },
  { dataIndex: 'processStatusName', title: 'สถานะภายใน' },
  { dataIndex: 'trackingNumber', title: 'เลขพัสดุ' },
  { dataIndex: 'shippingDate', title: 'วันที่จัดส่ง' },
  { dataIndex: 'note', title: 'หมายเหตุการเลื่อนเคลม' },
  { dataIndex: 'remark', title: 'หมายเหตุ' },
  { dataIndex: 'sendCsStatusLabel', title: 'ส่ง CS' },
  { dataIndex: 'refCsId', title: 'CS ID' },
  { dataIndex: 'refJournalId', title: 'Journal ID' },
  { dataIndex: 'stockStatusLabel', title: 'สถานะสต็อก' },
  { dataIndex: 'stockAvailableQty', title: 'สต็อกคงเหลือ' },
];

// คอลัมน์ที่แบรนด์นี้ไม่ใช้ตามสเปก export กลาง แม้ API จะส่งข้อมูลมาก็ให้เว้นว่าง
export const exportBlankFields = ['detail1', 'remark'];
